const CACHE_PREFIX = 'wiki_bio_';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Thrown when the Wikipedia page simply does not exist (not a network error). */
export class WikiNotFoundError extends Error {
  constructor(page: string) {
    super(`Wikipedia page not found: ${page}`);
    this.name = 'WikiNotFoundError';
  }
}

// Serbian Cyrillic → Latin with diacritics (proper Wikipedia Latin titles)
const CYRILLIC_TO_LATIN: { [key: string]: string } = {
  'а': 'a',  'б': 'b',  'в': 'v',  'г': 'g',  'д': 'd',  'ђ': 'đ',  'е': 'e',
  'ж': 'ž',  'з': 'z',  'и': 'i',  'ј': 'j',  'к': 'k',  'л': 'l',  'љ': 'lj',
  'м': 'm',  'н': 'n',  'њ': 'nj', 'о': 'o',  'п': 'p',  'р': 'r',  'с': 's',
  'т': 't',  'ћ': 'ć',  'у': 'u',  'ф': 'f',  'х': 'h',  'ц': 'c',  'ч': 'č',
  'џ': 'dž', 'ш': 'š',
  'А': 'A',  'Б': 'B',  'В': 'V',  'Г': 'G',  'Д': 'D',  'Ђ': 'Đ',  'Е': 'E',
  'Ж': 'Ž',  'З': 'Z',  'И': 'I',  'Ј': 'J',  'К': 'K',  'Л': 'L',  'Љ': 'Lj',
  'М': 'M',  'Н': 'N',  'Њ': 'Nj', 'О': 'O',  'П': 'P',  'Р': 'R',  'С': 'S',
  'Т': 'T',  'Ћ': 'Ć',  'У': 'U',  'Ф': 'F',  'Х': 'H',  'Ц': 'C',  'Ч': 'Č',
  'Џ': 'Dž', 'Ш': 'Š',
};

/**
 * Converts a Cyrillic Wikipedia page key (with underscores) to its Latin
 * equivalent, preserving underscores and capitalising each word segment.
 * Example: "Александар_Пушкин" → "Aleksandar_Puškin"
 */
export function cyrillicKeyToLatin(key: string): string {
  let result = '';
  for (const ch of key) {
    result += CYRILLIC_TO_LATIN[ch] ?? ch;
  }
  return result;
}

interface CachedBio {
  html: string;
  timestamp: number;
}

function getCachedBio(wikiPage: string): string | null {
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + wikiPage);
    if (cached) {
      const data: CachedBio = JSON.parse(cached);
      if (Date.now() - data.timestamp < CACHE_EXPIRY_MS) {
        return data.html;
      }
      localStorage.removeItem(CACHE_PREFIX + wikiPage);
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

function setCachedBio(wikiPage: string, html: string): void {
  try {
    const data: CachedBio = { html, timestamp: Date.now() };
    localStorage.setItem(CACHE_PREFIX + wikiPage, JSON.stringify(data));
  } catch {
    // Ignore cache errors
  }
}

function sanitizeHtml(html: string): string {
  // Use DOM parser for reliable manipulation
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Remove all images
  doc.querySelectorAll('img').forEach(el => el.remove());

  // Remove figure elements
  doc.querySelectorAll('figure').forEach(el => el.remove());

  // Remove edit section spans (contains "уреди" links)
  doc.querySelectorAll('.mw-editsection').forEach(el => el.remove());
  doc.querySelectorAll('[class*="editsection"]').forEach(el => el.remove());

  // Remove all tables
  doc.querySelectorAll('table').forEach(el => el.remove());

  // Remove reference links [1], [2], etc.
  doc.querySelectorAll('sup.reference').forEach(el => el.remove());
  doc.querySelectorAll('.reference').forEach(el => el.remove());
  doc.querySelectorAll('sup').forEach(el => {
    if (el.querySelector('a[href*="#cite"]')) {
      el.remove();
    }
  });

  // Remove specific div classes
  const unwantedClasses = ['navbox', 'reflist', 'references', 'gallery', 'thumb', 'toc', 'infobox', 'metadata', 'noprint', 'sistersitebox', 'mbox-small', 'mbox', 'ambox', 'catlinks'];
  unwantedClasses.forEach(cls => {
    doc.querySelectorAll(`[class*="${cls}"]`).forEach(el => el.remove());
  });

  // Remove elements by ID
  const unwantedIds = ['toc', 'catlinks', 'mw-navigation'];
  unwantedIds.forEach(id => {
    const el = doc.getElementById(id);
    if (el) el.remove();
  });

  // Remove unwanted sections by header text
  const unwantedSections = [
    'напомене', 'напомена',
    'референце', 'референца', 'reference',
    'литература', 'literatura',
    'спољашње везе', 'spoljašnje veze', 'spoljasnje veze',
    'види још', 'vidi još', 'vidi jos',
    'извори', 'извор', 'izvori', 'izvor',
    'галерија', 'galerija'
  ];

  doc.querySelectorAll('h2, h3').forEach(header => {
    const headerText = (header.textContent || '').toLowerCase().trim();
    const shouldRemove = unwantedSections.some(section =>
      headerText.includes(section)
    );

    if (shouldRemove) {
      // Remove this header and all siblings until next h2
      let sibling = header.nextElementSibling;
      while (sibling && sibling.tagName !== 'H2') {
        const next = sibling.nextElementSibling;
        sibling.remove();
        sibling = next;
      }
      header.remove();
    }
  });

  // Convert links to plain text
  doc.querySelectorAll('a').forEach(link => {
    const textContent = link.textContent || '';
    const textNode = doc.createTextNode(textContent);
    link.parentNode?.replaceChild(textNode, link);
  });

  // Get cleaned HTML
  let sanitized = doc.body.innerHTML;

  // Remove empty parentheses that remain after removing references
  sanitized = sanitized.replace(/\(\s*\)/g, '');
  // Remove any remaining bracket patterns like [уреди] or [1]
  sanitized = sanitized.replace(/\[\s*\d+\s*\]/g, '');
  sanitized = sanitized.replace(/\[\s*[Уу]реди[^\]]*\]/g, '');

  return sanitized;
}

export async function fetchWikipediaBio(wikiPage: string): Promise<string> {
  // Check cache first
  const cached = getCachedBio(wikiPage);
  if (cached) {
    return cached;
  }

  const encodedPage = encodeURIComponent(wikiPage);
  const url = `https://sr.wikipedia.org/w/api.php?action=parse&page=${encodedPage}&prop=text&format=json&origin=*`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Није могуће учитати биографију');
  }

  const data = await response.json();
  if (data.error) {
    throw new WikiNotFoundError(wikiPage);
  }

  const html = sanitizeHtml(data.parse.text['*']);
  setCachedBio(wikiPage, html);
  return html;
}

